'use client';

import { type ReactNode } from 'react';

import { type ChannelTypeValue } from '@flipster/apis/v1/channel-verification/types';
import { T } from '@flipster/i18n';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css, type Styles } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { channelIconMap, channelLabelKeyMap, channelValues } from '../constants';

const DropdownSelect = createDropdownSelect<ChannelTypeValue>();

const chevronClassName = css({
  transition: 'transform 200ms',
  '[aria-expanded=true] &': { transform: 'rotate(180deg)' },
});

interface ChannelDropdownProps {
  selected: ChannelTypeValue | undefined;
  onChange: (value: ChannelTypeValue) => void;
  placeholder?: ReactNode;
  css?: Styles;
}

export const ChannelDropdown = ({ selected, onChange, placeholder, css: cssProps }: ChannelDropdownProps) => (
  <DropdownSelect.Root selected={selected} onChange={onChange}>
    <DropdownSelect.Trigger asChild>
      <button
        type='button'
        className={css(
          hstack.raw({
            gap: 8,
            justify: 'space-between',
            borderColor: 'border.default.level-3',
            borderWidth: '1px',
            borderStyle: 'solid',
            rounded: 8,
            cursor: 'pointer',
          }),
          cssProps,
        )}
      >
        <div
          className={hstack({
            gap: 8,
            textStyle: 'Text/L/Regular',
            color: 'content.default.level-5',
            whiteSpace: 'nowrap',
          })}
        >
          {selected && channelIconMap[selected]}
          <p>{selected ? <T id={channelLabelKeyMap[selected]} /> : placeholder}</p>
        </div>
        <ChevronDownIcon size={16} color={token('colors.content.default.level-3')} className={chevronClassName} />
      </button>
    </DropdownSelect.Trigger>
    <DropdownSelect.Select portal>
      {channelValues.map((value) => (
        <DropdownSelect.Item
          key={value}
          value={value}
          label={<T id={channelLabelKeyMap[value]} />}
          icon={channelIconMap[value]}
        />
      ))}
    </DropdownSelect.Select>
  </DropdownSelect.Root>
);
